import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import KeyboardArrowLeftOutlined from '@mui/icons-material/KeyboardArrowLeftOutlined';
import KeyboardArrowRightOutlined from '@mui/icons-material/KeyboardArrowRightOutlined';
import type { Theme } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import Button from '@mui/material/Button';
import MuiCircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Unstable_Grid2';
import Box from '@mui/system/Box';
import * as React from 'react';
import { Image } from 'remix-image';
import DraggableCarousel from '~/components/Shop/DraggableCarousel';
import NoImageAvailable from '~/images/no_image_available.svg';

const MEDIA_PER_PAGE = 6;

type MediaSectionImage = {
  product: number;
  src: string;
  alt: string;
};
type IsLoading = (id: number) => boolean;

export function MediaSection({
  images,
  isLoading = () => false,
  isPack,
  isSafsiraProduct,
}: {
  images: MediaSectionImage[];
  isLoading?: IsLoading;
  isPack: boolean;
  isSafsiraProduct?: boolean;
}) {
  const [selectedImageId, setSelectedImageId] = React.useState(images[0]?.product);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const [page, setPage] = React.useState(1);
  const pages = Math.ceil(images.length / MEDIA_PER_PAGE);
  const handleChangePage = (newPage: number) => {
    if (newPage < 1 || newPage > pages) return;
    setPage(newPage);
  };

  const offset = (page - 1) * MEDIA_PER_PAGE;
  const limit = offset + MEDIA_PER_PAGE;
  const showArrows = pages > 1;

  const selectedImage = images.find((image) => image.product === selectedImageId) ??
    images[0] ?? { product: -1, src: NoImageAvailable, alt: '' };

  return (
    <section>
      <Stack
        spacing={3}
        bgcolor="rgba(0, 0, 0, 0.04)"
        borderRadius="8px"
        justifyContent="center"
        alignItems="center"
        p={2}
        mb={3}
        height={{ xs: 'auto', sm: '466px' }}
        position="relative"
      >
        {isSafsiraProduct && (
          <Tooltip title="Powered with Safsira Design Lab" placement="top" arrow>
            <BoltOutlinedIcon
              sx={{
                color: 'primary.main',
                fontSize: 30,
                position: 'absolute',
                top: 8,
                right: 8,
                '&:hover': { color: '#24598A' },
              }}
            />
          </Tooltip>
        )}
        <MainImage
          key={selectedImage.src}
          src={selectedImage.src}
          alt={selectedImage.alt}
          isLoading={isLoading(selectedImage.product)}
        />
      </Stack>

      {!isMobile && (
        <>
          <Grid container spacing={3} p={0} mb={4} minHeight={showArrows ? 330 : 'initial'}>
            {images.slice(offset, limit).map((image) => (
              <Grid key={image.product} xs={6} sm={4}>
                <SecondaryImage
                  image={image}
                  isLoading={isLoading}
                  selectedImage={selectedImage}
                  setSelectedImageId={setSelectedImageId}
                  imageDimension={122}
                />
              </Grid>
            ))}
          </Grid>
          {showArrows && (
            <Stack direction="row" justifyContent="space-between">
              <IconButton
                size="large"
                color="inherit"
                aria-label="user menu"
                aria-haspopup="true"
                disabled={page === 1}
                onClick={() => handleChangePage(page - 1)}
                sx={{ ml: 6, bgcolor: 'rgba(18, 18, 61, 0.04)' }}
              >
                <KeyboardArrowLeftOutlined />
              </IconButton>

              <IconButton
                size="large"
                color="inherit"
                aria-label="user menu"
                aria-haspopup="true"
                disabled={page === pages}
                onClick={() => handleChangePage(page + 1)}
                sx={{ mr: 6, bgcolor: 'rgba(18, 18, 61, 0.04)' }}
              >
                <KeyboardArrowRightOutlined />
              </IconButton>
            </Stack>
          )}
        </>
      )}
      {isPack && isMobile && (
        <DraggableCarousel spacing={0.5}>
          {images.map((image) => (
            <Box key={image.product} flex="0 0 auto" mr={0.5} width={91} height={91}>
              <SecondaryImage
                image={image}
                isLoading={isLoading}
                selectedImage={selectedImage}
                setSelectedImageId={setSelectedImageId}
                imageDimension={73}
              />
            </Box>
          ))}
        </DraggableCarousel>
      )}
    </section>
  );
}

function MainImage({ src, alt, isLoading = false }: { src: string; alt: string; isLoading?: boolean }) {
  return (
    <LoadingWrapper size={40} isLoading={isLoading}>
      {src ? (
        <Image
          src={src}
          alt={alt}
          responsive={[{ size: { width: 600, height: 600 } }]}
          options={{ fit: 'inside' }}
          dprVariants={[1, 3]}
          loading="lazy"
          width={400}
          height={400}
          style={{
            width: '100%',
            maxHeight: 434,
            height: 'auto',
            minWidth: 'auto',
            minHeight: 'auto',
          }}
        />
      ) : (
        <img src={NoImageAvailable} alt="no available" style={{ width: '100%', minWidth: 'auto', minHeight: 'auto' }} />
      )}
    </LoadingWrapper>
  );
}

function SecondaryImage({
  image,
  selectedImage,
  setSelectedImageId,
  isLoading,
  imageDimension,
}: {
  image: MediaSectionImage;
  isLoading: IsLoading;
  selectedImage: MediaSectionImage;
  setSelectedImageId: (value: React.SetStateAction<number>) => void;
  imageDimension: number;
}) {
  return (
    <Button
      onClick={() => setSelectedImageId(image.product)}
      sx={({ palette }) => ({
        p: 1,
        borderRadius: 2,
        bgcolor: 'rgba(0, 0, 0, 0.04)',
        width: '100%',
        border: image.product === selectedImage.product ? `1px solid ${palette.primary.main}` : '1px solid transparent',
        '&:hover': {
          border: `1px solid ${palette.primary.main}`,
          cursor: 'pointer',
        },
      })}
      disableRipple
    >
      <LoadingWrapper size={30} isLoading={isLoading(image.product)}>
        <Image
          key={image.src}
          src={image.src}
          alt={image.alt}
          responsive={[{ size: { height: imageDimension, width: imageDimension } }]}
          options={{ fit: 'inside' }}
          dprVariants={[1, 3]}
          loading="lazy"
          width={imageDimension}
          height={imageDimension}
          style={{
            width: '100%',
            minWidth: 'auto',
            minHeight: 'auto',
            maxWidth: imageDimension,
            maxHeight: imageDimension,
          }}
        />
      </LoadingWrapper>
    </Button>
  );
}

export function LoadingWrapper({
  children,
  size = 25,
  isLoading = false,
}: {
  children: React.ReactNode;
  size?: number;
  isLoading?: boolean;
}) {
  return (
    <Stack justifyContent="center" alignItems="center" position="relative">
      <Stack sx={{ opacity: isLoading ? 0.3 : 1 }}>{children}</Stack>
      {isLoading && <MuiCircularProgress size={size} sx={{ position: 'absolute' }} />}
    </Stack>
  );
}
